import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUser,
  FaRulerCombined,
  FaLayerGroup,
  FaImages,
  FaVideo,
  FaBuilding,
  FaCheckCircle,
} from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import "./ProjectDetails.css";

const ProjectDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const [project, setProject] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /* =====================================================
     LOAD PROJECT
  ===================================================== */

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);

        setError("");

        console.log("Loading project ID:", id);

        if (!id) {
          throw new Error("Project ID is missing.");
        }

        const projectRef = doc(db, "projects", id);

        const projectSnapshot = await getDoc(projectRef);

        if (!projectSnapshot.exists()) {
          throw new Error("Project not found in Firebase.");
        }

        const projectData = {
          id: projectSnapshot.id,
          ...projectSnapshot.data(),
        };

        console.log("Project loaded:", projectData);

        setProject(projectData);
      } catch (err) {
        console.error("PROJECT DETAILS ERROR:", err);

        setError(err.message || "Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="project-details-page">
        <div className="project-details-loading">
          <div className="loading-spinner"></div>

          <h2>Loading Project...</h2>

          <p>Please wait while we load the project details.</p>
        </div>
      </div>
    );
  }

  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <div className="project-details-page">
        <button
          className="details-back-button"
          onClick={() => navigate("/admin/projects")}
        >
          <FaArrowLeft />
          Back to Projects
        </button>

        <div className="project-details-error">
          <h2>Unable to Load Project</h2>

          <p>{error}</p>

          <button onClick={() => navigate("/admin/projects")}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  /* =====================================================
     PROJECT NOT FOUND
  ===================================================== */

  if (!project) {
    return (
      <div className="project-details-page">
        <div className="project-details-error">
          <h2>Project Not Found</h2>

          <p>This project does not exist in Firebase.</p>

          <button onClick={() => navigate("/admin/projects")}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  /* =====================================================
     MEDIA DATA
  ===================================================== */

  const coverImage =
    project.coverImage || project.coverImageUrl || project.image || "";

  const galleryImages = Array.isArray(project.galleryImages)
    ? project.galleryImages
    : [];

  const video = project.video || project.videoUrl || "";

  /* =====================================================
     DATE FORMAT
  ===================================================== */

  const formatDate = (date) => {
    if (!date) return "Not specified";

    try {
      const parsedDate = new Date(date);

      if (isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="project-details-page">
      {/* =================================================
          BACK BUTTON
      ================================================= */}

      <button
        className="details-back-button"
        onClick={() => navigate("/admin/projects")}
      >
        <FaArrowLeft />
        Back to Projects
      </button>

      {/* =================================================
          HERO
      ================================================= */}

      <div className="project-details-hero">
        {/* COVER IMAGE */}

        <div className="project-details-cover">
          {coverImage ? (
            <img src={coverImage} alt={project.title || "Project"} />
          ) : (
            <div className="details-no-image">
              <FaImages />

              <span>No Cover Image</span>
            </div>
          )}

          {/* FEATURED */}

          {project.featured && (
            <div className="details-featured">
              <FaCheckCircle />
              Featured Project
            </div>
          )}
        </div>

        {/* HERO INFORMATION */}

        <div className="project-details-hero-content">
          <span className="details-sport">
            {project.sport || "Sports Project"}
          </span>

          <h1>{project.title || "Untitled Project"}</h1>

          {project.shortDescription && (
            <p className="details-short-description">
              {project.shortDescription}
            </p>
          )}

          <div className="details-location">
            <FaMapMarkerAlt />

            <span>
              {project.location || "Location not specified"}

              {project.state ? `, ${project.state}` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* =================================================
          PROJECT INFORMATION
      ================================================= */}

      <div className="project-details-layout">
        {/* =================================================
            MAIN CONTENT
        ================================================= */}

        <div className="project-details-main">
          {/* DESCRIPTION */}

          <section className="details-section">
            <div className="details-section-header">
              <h2>Project Description</h2>
            </div>

            {project.description ? (
              <p className="details-description">{project.description}</p>
            ) : project.shortDescription ? (
              <p className="details-description">{project.shortDescription}</p>
            ) : (
              <p className="details-muted">No project description available.</p>
            )}
          </section>

          {/* GALLERY */}

          <section className="details-section">
            <div className="details-section-header">
              <div>
                <h2>Project Gallery</h2>

                <p>Project images</p>
              </div>

              <span className="gallery-count">
                {galleryImages.length} Images
              </span>
            </div>

            {galleryImages.length > 0 ? (
              <div className="details-gallery">
                {galleryImages.map((image, index) => {
                  /*
                      Supports both:

                      galleryImages: [
                        "https://..."
                      ]

                      OR

                      galleryImages: [
                        {
                          url: "https://..."
                        }
                      ]
                    */

                  const imageUrl =
                    typeof image === "string"
                      ? image
                      : image?.url || image?.secure_url || "";

                  if (!imageUrl) {
                    return null;
                  }

                  return (
                    <div className="details-gallery-item" key={index}>
                      <img
                        src={imageUrl}
                        alt={`${project.title || "Project"} ${index + 1}`}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="details-empty-media">
                <FaImages />

                <p>No gallery images available.</p>
              </div>
            )}
          </section>

          {/* VIDEO */}

          <section className="details-section">
            <div className="details-section-header">
              <div>
                <h2>Project Video</h2>

                <p>Project construction video</p>
              </div>
            </div>

            {video ? (
              <div className="details-video">
                <video src={video} controls preload="metadata" />
              </div>
            ) : (
              <div className="details-empty-media">
                <FaVideo />

                <p>No project video available.</p>
              </div>
            )}
          </section>
        </div>

        {/* =================================================
            SIDEBAR
        ================================================= */}

        <aside className="project-details-sidebar">
          {/* PROJECT INFORMATION */}

          <div className="details-info-card">
            <h3>Project Information</h3>

            <div className="details-info-list">
              {/* SPORT */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaBuilding />
                </div>

                <div>
                  <span>Sport</span>

                  <strong>{project.sport || "Not specified"}</strong>
                </div>
              </div>

              {/* SURFACE */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaLayerGroup />
                </div>

                <div>
                  <span>Surface</span>

                  <strong>{project.surface || "Not specified"}</strong>
                </div>
              </div>

              {/* CLIENT */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaUser />
                </div>

                <div>
                  <span>Client</span>

                  <strong>{project.client || "Not specified"}</strong>
                </div>
              </div>

              {/* LOCATION */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaMapMarkerAlt />
                </div>

                <div>
                  <span>Location</span>

                  <strong>{project.location || "Not specified"}</strong>
                </div>
              </div>

              {/* DATE */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaCalendarAlt />
                </div>

                <div>
                  <span>Completion Date</span>

                  <strong>{formatDate(project.completionDate)}</strong>
                </div>
              </div>

              {/* AREA */}

              <div className="details-info-item">
                <div className="details-info-icon">
                  <FaRulerCombined />
                </div>

                <div>
                  <span>Total Area</span>

                  <strong>
                    {project.area ? `${project.area} sq.ft` : "Not specified"}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* DIMENSIONS */}

          <div className="details-info-card">
            <h3>Dimensions</h3>

            <div className="dimensions-grid">
              <div>
                <span>Length</span>

                <strong>
                  {project.courtLength ? `${project.courtLength} ft` : "—"}
                </strong>
              </div>

              <div>
                <span>Width</span>

                <strong>
                  {project.courtWidth ? `${project.courtWidth} ft` : "—"}
                </strong>
              </div>

              <div>
                <span>Area</span>

                <strong>{project.area ? `${project.area} sq.ft` : "—"}</strong>
              </div>
            </div>
          </div>

          {/* SEO */}

          <div className="details-info-card">
            <h3>SEO Information</h3>

            <div className="seo-info">
              <div>
                <span>SEO Title</span>

                <p>{project.seoTitle || "Not specified"}</p>
              </div>

              <div>
                <span>SEO Description</span>

                <p>{project.seoDescription || "Not specified"}</p>
              </div>
            </div>
          </div>

          {/* EDIT BUTTON */}

          <button
            className="details-edit-button"
            onClick={() => navigate(`/admin/projects/${project.id}/edit`)}
          >
            Edit Project
          </button>
        </aside>
      </div>
    </div>
  );
};

export default ProjectDetails;
